/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Card, Table, Button, Form } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { formatFloatInput } from '../../util/util';

interface Props {
  draft: any;
  setDraft: (v: any) => void;
  logic: Record<string, any>;
  setLogic: (v: any) => void;
}

const ChoiceOptionsEditor: React.FC<Props> = ({
  draft,
  setDraft,
  logic,
  setLogic,
}) => {
  if (draft.type !== 'single_choice' && draft.type !== 'multi_choice') {
    return null;
  }

  const updateScore = (value: string, score: number | string) => {
    setLogic({
      ...logic,
      options: {
        ...(logic.options || {}),
        [value]: score,
      },
    });
  };

  return (
    <Card body className="bg-light mb-3">
      <Card.Title>Choice Options</Card.Title>
      <span className="fs-6 text-muted mb-2 d-block">
        Define the selectable options and score for each one.
      </span>

      <Table size="sm" className="table-transparent table-borderless">
        <thead>
          <tr>
            <th></th>
            <th style={{ width: '75%' }}>Label</th>
            <th style={{ width: '150px' }}>Score</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(draft.options || []).map((opt: any, idx: number) => (
            <tr key={opt.value} className="align-middle">
              <td className="p-0">
                <div className="d-flex gap-1">
                  {/* Move Up */}
                  <Button
                    variant="link"
                    className="p-0"
                    disabled={idx === 0}
                    onClick={() => {
                      if (idx === 0) return;
                      const newOptions = [...draft.options];
                      [newOptions[idx - 1], newOptions[idx]] = [
                        newOptions[idx],
                        newOptions[idx - 1],
                      ];
                      setDraft({ ...draft, options: newOptions });
                    }}
                  >
                    <i className="bi bi-arrow-up-square" />
                  </Button>

                  {/* Move Down */}
                  <Button
                    variant="link"
                    className="p-0"
                    disabled={idx === draft.options.length - 1}
                    onClick={() => {
                      if (idx === draft.options.length - 1) return;
                      const newOptions = [...draft.options];
                      [newOptions[idx + 1], newOptions[idx]] = [
                        newOptions[idx],
                        newOptions[idx + 1],
                      ];
                      setDraft({ ...draft, options: newOptions });
                    }}
                  >
                    <i className="bi bi-arrow-down-square" />
                  </Button>
                </div>
              </td>

              {/* Label */}
              <td>
                <Form.Control
                  type="text"
                  value={opt.label}
                  onChange={(e) => {
                    const updated = draft.options.map((o: any) =>
                      o.value === opt.value
                        ? { ...o, label: e.target.value }
                        : o
                    );
                    setDraft({ ...draft, options: updated });
                  }}
                  placeholder="Option label"
                />
              </td>

              {/* Score */}
              <td>
                <Form.Control
                  type="text"
                  value={logic.options?.[opt.value] ?? opt.score ?? 0}
                  onChange={(e) => {
                    const score = formatFloatInput(e.target.value);
                    // Update draft option label too
                    const updatedOpts = draft.options.map((o: any) =>
                      o.value === opt.value ? { ...o, score } : o
                    );
                    setDraft({ ...draft, options: updatedOpts });
                    updateScore(opt.value, score);
                  }}
                  placeholder="0.5"
                />
              </td>

              {/* Delete */}
              <td>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="editor-icon-button"
                  onClick={() => {
                    const newOptions = draft.options.filter(
                      (o: any) => o.value !== opt.value
                    );
                    setDraft({ ...draft, options: newOptions });

                    const newLogic = { ...logic };
                    if (newLogic.options) delete newLogic.options[opt.value];
                    setLogic(newLogic);
                  }}
                >
                  ✕
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => {
          const id = uuidv4();
          const newOption = {
            value: id,
            label: 'New Option',
            score: 0,
            ordinal: (draft.options?.length ?? 0) + 1,
          };
          const newOptions = [...(draft.options || []), newOption];
          setDraft({ ...draft, options: newOptions });
          setLogic({
            ...logic,
            options: { ...(logic.options || {}), [id]: 0 },
          });
        }}
      >
        + Add Option
      </Button>
    </Card>
  );
};

export default ChoiceOptionsEditor;
